import { Button } from "@/lib/components/Button/Button";
import { cn, formatAudioDuration } from "@/lib/utils";
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import WavesurferPlayer from "@wavesurfer/react";
import type WaveSurfer from "wavesurfer.js";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";

function WaveformPlayer({
  url,
  peaks,
  durationMs,
  isPlaying,
  onPlay,
  onPause,
  videoIsPlaying,
  className,
  volume = 1,
  durationWarning,
}: {
  url: string;
  peaks?: number[][];
  durationMs: number;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  videoIsPlaying: boolean;
  className?: string;
  volume?: number;
  durationWarning?: string;
}) {
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const onReady = (ws: WaveSurfer) => {
    setWavesurfer(ws);
    setIsLoading(false);

    const applyGradient = () => {
      const canvas = ws.getWrapper().querySelector("canvas");
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);

      gradient.addColorStop(0, "#f98c5e");
      gradient.addColorStop(1, "#f66c97");

      ws.setOptions({
        progressColor: gradient,
      });
    };

    applyGradient();
    ws.on("redraw", applyGradient);
  };

  useEffect(() => {
    setIsLoading(true);
    setWavesurfer(null);
  }, [url]);

  const onPlayPause = () => {
    if (wavesurfer && !isLoading) {
      wavesurfer.playPause();
    }
  };

  useEffect(() => {
    if (!wavesurfer || isLoading) return;

    const isWavesurferPlaying = wavesurfer.isPlaying();

    if (isPlaying && !isWavesurferPlaying) {
      wavesurfer.play();
    } else if (!isPlaying && isWavesurferPlaying) {
      wavesurfer.pause();
    }
  }, [isPlaying, wavesurfer, isLoading]);

  useEffect(() => {
    if (videoIsPlaying && wavesurfer && wavesurfer.isPlaying()) {
      wavesurfer.pause();
    }
  }, [videoIsPlaying, wavesurfer]);

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.setVolume(volume);
    }
  }, [volume, wavesurfer]);

  return (
    <div
      className={cn(
        "mt-3 flex min-h-13 items-center gap-1 rounded-xl border border-grey-200 px-4",
        className,
      )}
    >
      <Button
        onClick={onPlayPause}
        disabled={isLoading}
        className="-ml-3 text-text-primary hover:bg-white disabled:opacity-100"
        variant="ghost"
        size="icon"
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : isPlaying ? (
          <IconPlayerPauseFilled size={24} />
        ) : (
          <IconPlayerPlayFilled size={24} />
        )}
      </Button>
      <div className="relative flex-1">
        <WavesurferPlayer
          height={24}
          waveColor="#e5e7eb"
          progressColor="#f98c5e"
          url={url}
          peaks={peaks}
          onReady={onReady}
          onPlay={onPlay}
          onPause={onPause}
          barWidth={1.5}
          barGap={3}
          barRadius={3}
          cursorColor="transparent"
          normalize={true}
        />
      </div>
      {durationWarning ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-regular ml-1 shrink-0 cursor-default text-sm text-error">
              {formatAudioDuration(durationMs)}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-center whitespace-normal">
            {durationWarning}
          </TooltipContent>
        </Tooltip>
      ) : (
        <span className="font-regular ml-1 shrink-0 text-sm text-black-400">
          {formatAudioDuration(durationMs)}
        </span>
      )}
    </div>
  );
}

export default WaveformPlayer;
